import { FC, ReactElement, useState } from 'react';

import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import styles from './attachment-link.module.scss';

import {
  formatFileSize,
  getAttachmentPreviewCapability,
  getFileTypeCategory,
} from 'utils/attachments';
import { AttachmentPreviewModal } from 'views/components/UI/AttachmentPreview';
import { Button } from 'views/components/UI/Button';
import { FILE_TYPE_ICON } from 'views/components/UI/Icons/fileTypeIcons';
import { CloseSmall } from 'views/components/UI/Icons/Icons';

interface IAttachmentLinkProps {
  fileName?: string;
  contentType: string;
  sizeBytes: number;
  requestDownloadUrl: () => Promise<string>;
  onRemove?: () => void;
}

const AttachmentLink: FC<IAttachmentLinkProps> = ({
  fileName,
  contentType,
  sizeBytes,
  requestDownloadUrl,
  onRemove,
}): ReactElement => {
  const intl = useIntl();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const previewFileName =
    fileName || intl.formatMessage({ id: 'attachments.generic-name' });
  const previewCapability = getAttachmentPreviewCapability({
    contentType,
    fileName: previewFileName,
    sizeBytes,
  });

  const handleAttachmentClick = async (): Promise<void> => {
    if (previewCapability.isPreviewable) {
      setIsPreviewOpen(true);
      return;
    }

    try {
      setIsDownloading(true);
      const downloadUrl = await requestDownloadUrl();
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setIsDownloading(false);
    }
  };

  const category = getFileTypeCategory(contentType, previewFileName);
  const FileIcon = FILE_TYPE_ICON[category];

  return (
    <div className={styles.wrapper}>
      <button
        type="button"
        onClick={handleAttachmentClick}
        disabled={isDownloading}
        className={styles.chip}
        aria-label={intl.formatMessage({
          id: previewCapability.isPreviewable
            ? 'attachments.preview'
            : 'attachments.download',
        })}
      >
        <span
          className={classNames(styles.iconBox, styles[`iconBox--${category}`], {
            [styles['iconBox--other']]:
              category === 'document' && !previewCapability.category,
          })}
        >
          <FileIcon size="24px" />
        </span>
        <span className={styles.details}>
          <span className={styles.fileName}>
            {fileName || <FormattedMessage id="attachments.generic-name" />}
          </span>
          <span className={styles.size}>{formatFileSize(sizeBytes)}</span>
        </span>
      </button>
      {onRemove && (
        <Button
          type="button"
          mode="tertiary"
          size="small"
          icon={<CloseSmall />}
          onClick={onRemove}
          aria-label={intl.formatMessage({ id: 'attachments.remove' })}
        />
      )}
      {previewCapability.isPreviewable && (
        <AttachmentPreviewModal
          contentType={contentType}
          fileName={previewFileName}
          handleClose={() => setIsPreviewOpen(false)}
          isOpen={isPreviewOpen}
          requestDownloadUrl={requestDownloadUrl}
          sizeBytes={sizeBytes}
        />
      )}
    </div>
  );
};

export { AttachmentLink };
